import React, { useState, useEffect, useRef } from "react";
import { FaSortDown, FaSortUp, FaStore } from "react-icons/fa";
import { Check } from "lucide-react";
import sampleLogo from "../assets/sample_org.png";
import { PiDotsThreeVertical, PiDotsThreeVerticalBold } from "react-icons/pi";

const OrganizationDropdown = ({
  organizations = [],
  currentOrganization,
  onOrganizationChange,
  onThreeDotsClick,
  windowWidth,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOrganizationSelect = (org) => {
    // Store selected organization in localStorage
    localStorage.setItem("organization", JSON.stringify(org));

    // Call the parent's change handler
    if (onOrganizationChange) {
      onOrganizationChange(org);
    }

    setIsOpen(false);

    // Reload the page to update the organization context
    window.location.reload();
  };

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  };

  const maxNameLength = windowWidth > 1024 ? 25 : 15;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Trigger */}
      <div
        className="flex items-center py-1 px-2 gap-2 rounded-xl border-[1px] border-text-clr cursor-pointer hover:bg-gray-50 transition-colors"
      // onClick={() => setIsOpen(!isOpen)}
      >
        <div onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? (
            <FaSortUp className="text-gray-600" />
          ) : (
            <FaSortDown className="text-gray-600" />
          )}
        </div>
        {currentOrganization?.logo_image_file ? (
          <img
            src={currentOrganization?.logo_image_file}
            alt="Organization logo"
            className="h-6 w-6 md:h-8 md:w-8 rounded-full border-1 border-[#737373] object-cover"
          />
        ) : (
          <div className="h-6 w-6 md:h-8 md:w-8 rounded-full border-1 border-sm-primary flex items-center justify-center">
            <FaStore className="text-sm-primary" />
          </div>
        )}
        <p
          className="text-[14px] md:text-[18px] font-semibold text-gray-800"
          title={currentOrganization?.organization_name}
        >
          {truncateText(currentOrganization?.organization_name, maxNameLength)}
        </p>
        {onThreeDotsClick && (
          <PiDotsThreeVerticalBold onClick={onThreeDotsClick} size={24} />
        )}
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-64 overflow-y-auto">
          <div className="py-2">
            {organizations.length > 0 ? (
              organizations.map((org) => (
                <div
                  key={org.id}
                  className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${currentOrganization?.id === org.id ? "bg-blue-50" : ""
                    }`}
                  onClick={() => handleOrganizationSelect(org)}
                >
                  {org.logo_image_file ? (
                    <img
                      src={org.logo_image_file}
                      alt={`${org.organization_name} logo`}
                      className="h-8 w-8 rounded-full border border-gray-200 object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full border-[1px] bg-white border-sm-primary flex items-center justify-center">
                      <FaStore className="text-sm-primary" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {org.organization_name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {org.business_address}
                    </p>
                  </div>
                  {currentOrganization?.id === org.id && (
                    <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  )}
                </div>
              ))
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No organizations found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationDropdown;
